<script lang="ts" setup>
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import DatamapperTableRow from '@/components/app/cases/DatamapperTableRow.vue'
  import { computed, type PropType, reactive, ref } from 'vue'
  import type {
    CustomerCase,
    CustomerCaseDataChange,
    CustomerEntityWithRelationship,
    CustomerEntityChange,
    TDataMapper,
    ProposedEntityChange
  } from '@/types/generated'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChevronUp, ChevronDown } from 'lucide-vue-next'
  import { yup } from '@/lib/validation'

  const { t } = useTranslation({
    en: {
      loadingData: 'Loading data',
      rechtsgebiet: 'Legal area',
      feld: 'Field',
      wert: 'Value',
      quelle: 'Source',
      aktualisiert: 'Updated',
      nurFelderMitWertenAnzeigen: 'Show only fields with values',
      updatingData: 'Updating data'
    },
    de: {
      loadingData: 'Lade Daten',
      rechtsgebiet: 'Rechtsgebiet',
      feld: 'Feld',
      wert: 'Wert',
      quelle: 'Quelle',
      aktualisiert: 'Aktualisiert',
      nurFelderMitWertenAnzeigen: 'Nur Felder mit Werten anzeigen',
      updatingData: 'Daten werden aktualisiert'
    }
  })

  const props = defineProps({
    isEntity: {
      type: Boolean,
      required: false,
      default: false
    },
    mappers: {
      type: Object as PropType<TDataMapper[]>,
      required: true
    },
    object: {
      type: Object as PropType<CustomerEntityWithRelationship | CustomerCase>,
      required: true
    },
    showKeys: {
      type: Array as PropType<string[]>,
      required: true
    },
    mandatoryKeys: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => []
    },
    customValidators: {
      type: Object as PropType<Record<string, yup.AnySchema> | null>,
      required: false,
      default: () => {}
    },
    mode: {
      type: String as PropType<'view' | 'edit'>,
      required: false,
      default: 'view'
    },
    ghost: {
      type: Boolean,
      required: false,
      default: false
    },
    sorting: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const safeCustomValidators = computed(() => props.customValidators || {})

  // Sorting state
  const sortField = ref<string>('aktualisiert')
  const sortDirection = ref<'asc' | 'desc'>('desc')

  const changes = computed<CustomerEntityChange[] | CustomerCaseDataChange[]>(() => {
    if (props.isEntity) {
      return props.object?.entity?.changes || null
    }
    return props.object?.data?.changes || null
  })

  const proposedChanges = computed<ProposedEntityChange[]>(() => {
    return (props.object as CustomerEntityWithRelationship)?.entity?.proposed_changes || null
  })

  const showTable = computed<boolean>(() => {
    return !!props.object && !!props.mappers && props.mappers.length > 0
  })

  const skeletonItems = computed<string[]>(() => {
    if (props.showKeys.length > 0) {
      return Array.from({ length: props.showKeys.length }, (_, index) => `item-${index}`)
    }
    return Array.from({ length: 10 }, (_, index) => `item-${index}`)
  })

  const onValueChange = (key: string, mapperKey: string, value: any) => {
    emit('onValueChange', key, mapperKey, value)
  }
  const handleValueChangeError = (mapperKey: string, isError: boolean) => {
    emit('valueChangeError', mapperKey, isError)
  }

  const handleAcceptProposedChange = (proposedChange: ProposedEntityChange) => {
    emit('acceptProposedChange', proposedChange)
  }

  const handleRejectProposedChange = (proposedChange: ProposedEntityChange) => {
    emit('rejectProposedChange', proposedChange)
  }
  const emit = defineEmits([
    'onValueChange',
    'valueChangeError',
    'acceptProposedChange',
    'rejectProposedChange'
  ])

  // Get latest change date for each mapper key
  const getLatestChangeDate = (mapperKey: string): Date | null => {
    if (!changes.value) return null

    const relevantChanges = props.isEntity
      ? changes.value.filter((change: any) => change.entities_mapper_key === mapperKey)
      : changes.value.filter((change: any) => change.mapper_key === mapperKey)

    if (relevantChanges.length === 0) return null

    const latestChange = relevantChanges.sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]

    return latestChange ? new Date(latestChange.created_at) : null
  }

  // Sort the mapper keys based on the current sort field and direction
  const sortedShowKeys = computed(() => {
    // Use Set to avoid duplicates and prevent empty values after sorting while showing all mappers
    if (sortField.value !== 'aktualisiert') {
      return new Set(props.showKeys)
    }

    const sortedArray = [...props.showKeys].sort((a, b) => {
      const dateA = getLatestChangeDate(a)
      const dateB = getLatestChangeDate(b)

      // Handle cases where one or both dates are null
      if (!dateA && !dateB) return 0
      if (!dateA) return sortDirection.value === 'asc' ? -1 : 1
      if (!dateB) return sortDirection.value === 'asc' ? 1 : -1

      // Sort by date
      const comparison = dateA.getTime() - dateB.getTime()
      return sortDirection.value === 'asc' ? comparison : -comparison
    })

    return new Set(sortedArray)
  })
  // Toggle sort when clicking on a sortable column header
  const toggleSort = (field: string) => {
    if (sortField.value === field) {
      // Toggle direction if already sorting by this field
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
      // Set new sort field and default to descending (newest first)
      sortField.value = field
      sortDirection.value = 'desc'
    }
  }
</script>
<template>
  <Table
    :class="[
      'w-full table-fixed',
      {
        [`[&>div]:!border-0 [&_table]:border-separate [&_td]:!border-0 [&_th]:!border-0
        [&_tr:nth-child(even)]:bg-slate-50 [&_tr]:!border-0`]: ghost
      }
    ]"
  >
    <TableHeader
      v-if="!ghost"
      :class="{ 'h-6': ghost }"
    >
      <TableRow>
        <TableCell
          v-if="!isEntity"
          :class="['w-[10%]', { '!h-6 !p-1': ghost }]"
        >
          {{ t('rechtsgebiet') }}
        </TableCell>
        <TableCell
          :class="[isEntity ? 'w-[15%]' : 'w-[30%]', 'font-medium', { '!h-6 !p-1': ghost }]"
        >
          {{ t('feld') }}
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[40%]' : 'w-[25%]', { '!h-6 !p-1': ghost }]">
          {{ t('wert') }}
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[20%]' : 'w-[25%]', { '!h-6 !p-1': ghost }]">
          {{ t('quelle') }}
        </TableCell>
        <TableCell
          :class="[isEntity ? 'w-[10%]' : 'w-[10%]', { '!h-6 !p-1': ghost }, 'cursor-pointer']"
          @click="toggleSort('aktualisiert')"
        >
          <div class="flex items-center">
            {{ t('aktualisiert') }}
            <span class="ml-1">
              <ChevronUp
                v-if="sortField === 'aktualisiert' && sortDirection === 'asc'"
                class="h-4 w-4"
              />
              <ChevronDown
                v-else
                class="h-4 w-4"
              />
            </span>
          </div>
        </TableCell>
      </TableRow>
    </TableHeader>
    <TableBody v-if="showTable">
      <template v-if="!sorting">
        <DatamapperTableRow
          v-for="mapper in showKeys"
          :key="mapper"
          :object="object"
          :is-entity="isEntity"
          :is-mandatory="mandatoryKeys.includes(mapper)"
          :custom-validator="safeCustomValidators[mapper]"
          :all-changes="changes"
          :proposed-changes="proposedChanges"
          :mapper-key="mapper"
          :mappers="mappers"
          :mode="mode"
          @on-value-change="onValueChange"
          @value-change-error="handleValueChangeError"
          @accept-proposed-change="handleAcceptProposedChange"
          @reject-proposed-change="handleRejectProposedChange"
        />
      </template>
      <template v-else>
        <DatamapperTableRow
          v-for="mapper in Array.from(sortedShowKeys)"
          :key="mapper"
          :object="object"
          :is-entity="isEntity"
          :is-mandatory="mandatoryKeys.includes(mapper)"
          :custom-validator="safeCustomValidators[mapper]"
          :all-changes="changes"
          :proposed-changes="proposedChanges"
          :mapper-key="mapper"
          :mappers="mappers"
          :mode="mode"
          @on-value-change="onValueChange"
          @value-change-error="handleValueChangeError"
          @accept-proposed-change="handleAcceptProposedChange"
          @reject-proposed-change="handleRejectProposedChange"
        />
      </template>
    </TableBody>
    <TableBody v-else>
      <TableRow
        v-for="item in skeletonItems"
        :key="item"
      >
        <TableCell
          v-if="!isEntity"
          class="h-[72px] w-[10%]"
        >
          <SkeletonLoader class="h-4 w-16" />
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[15%]' : 'w-[30%]', 'h-[72px]']">
          <SkeletonLoader class="h-4 w-16" />
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[40%]' : 'w-[25%]', 'h-[72px]']">
          <SkeletonLoader class="h-4 w-32" />
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[20%]' : 'w-[25%]', 'h-[72px]']">
          <SkeletonLoader class="h-4 w-24" />
        </TableCell>
        <TableCell :class="[isEntity ? 'w-[10%]' : 'w-[10%]', 'h-[72px]']">
          <SkeletonLoader class="h-4 w-16" />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
